import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { ContentLangControl } from './Tx.js';
import { LangSwitch, useT } from '../i18n/index.js';
import './AppShell.de.js';
import './AppShell.fr.js';
import { NotificationBell } from 'notification-kit/client/index.js';
import { useTheme } from '../context/ThemeContext.js';
import { useEngagement } from '../context/EngagementContext.js';
import { PAGES, type PageId } from '../pages/registry.js';
import { fetchActivity, getLastSeen, markSeen, baselineIfFresh, type ActivityMap, type TabActivity } from '../api/activity.js';

const ACTIVITY_POLL_MS = 15_000;
const SEEN_REFRESH_MS = 20_000;
const ALL_TAB_IDS = PAGES.map((p) => p.id);

// The phone/tablet line (see public/styles/attest.css PHONE section): below it the sidebar
// goes off-canvas and opens as a drawer instead of the desktop collapse-to-48px toggle.
const PHONE_QUERY = '(max-width: 820px)';

/** Tracks the phone breakpoint live (resize, rotation) — not just at first paint. */
function usePhoneBreakpoint(): boolean {
  const [isPhone, setIsPhone] = useState(() => {
    try { return typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches; }
    catch { return false; }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try { mq = window.matchMedia(PHONE_QUERY); } catch { return; }
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isPhone;
}

const NAV_GROUPS: Array<'Overview' | 'CRM' | 'Engagement' | 'System'> = ['Overview', 'CRM', 'Engagement', 'System'];

// What each tab's count actually counts — surfaced as the badge's tooltip so a bare
// number never leaves the operator guessing. [singular, plural].
const COUNT_HINT: Record<string, [string, string]> = {
  // Proposals lives on the Agent tab now, so its queue count folds into that entry.
  agent: ['pending proposal to review or run in progress', 'pending proposals to review or runs in progress'],
  documents: ['document awaiting OCR', 'documents awaiting OCR'],
  register: ['open question to resolve', 'open questions to resolve'],
  contracting: ['change request awaiting a decision', 'change requests awaiting a decision'],
  acceptance: ['defect being worked', 'defects being worked'],
};

/**
 * The activity server still reports 'proposals' as its own tab; the sidebar entry it fed is
 * gone (the queue is the Agent page's sub-tab), so its count and freshness fold into the
 * Agent entry rather than vanishing. Timestamps are strict ISO-8601 UTC (the server
 * normalizes), so the lexicographic max is the later one.
 */
function foldActivity(a: TabActivity | undefined, b: TabActivity | undefined): TabActivity | undefined {
  if (!a || !b) return a ?? b;
  return {
    count: a.count + b.count,
    latest: !a.latest ? b.latest : !b.latest ? a.latest : (a.latest > b.latest ? a.latest : b.latest),
  };
}

export default function AppShell({ currentPage, children }: { currentPage: PageId; children: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { engagements, current, setCurrentId } = useEngagement();
  const [collapsed, setCollapsed] = useState(false);
  const isPhone = usePhoneBreakpoint();
  // Off-canvas nav drawer — phone/tablet only; the desktop collapse toggle (`collapsed`)
  // stays untouched above the breakpoint.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activity, setActivity] = useState<ActivityMap>({});
  // lastSeen lives in localStorage; this counter just forces a re-render when it changes.
  const [, setSeenTick] = useState(0);

  const engagementId = current?.id ?? null;

  // Growing back past the breakpoint (rotate, resize, external monitor) always leaves the
  // drawer closed rather than stuck open behind a now-static sidebar.
  useEffect(() => { if (!isPhone) setDrawerOpen(false); }, [isPhone]);

  // While the drawer is open: Escape closes it, and the body can't scroll behind it.
  useEffect(() => {
    if (!isPhone || !drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isPhone, drawerOpen]);

  const toggleSidebar = () => { if (isPhone) setDrawerOpen((v) => !v); else setCollapsed((v) => !v); };
  const closeDrawer = () => setDrawerOpen(false);

  // Poll per-tab activity every 15s and whenever the route or engagement changes.
  useEffect(() => {
    if (!engagementId) { setActivity({}); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const tabs = await fetchActivity(engagementId);
        if (cancelled) return;
        baselineIfFresh(engagementId, ALL_TAB_IDS); // first contact: no dot avalanche from history
        setActivity(tabs);
      } catch { /* transient (logout, server restart) — keep last known state, stay quiet */ }
    };
    void load();
    const timer = setInterval(load, ACTIVITY_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [engagementId, currentPage]);

  // The open tab is by definition "seen": stamp on entry and keep refreshing while the
  // operator stays on it, so their own actions never leave a stale dot behind.
  useEffect(() => {
    if (!engagementId) return;
    const stamp = () => { markSeen(engagementId, currentPage); setSeenTick((t) => t + 1); };
    stamp();
    const timer = setInterval(stamp, SEEN_REFRESH_MS);
    return () => { clearInterval(timer); stamp(); }; // stamp once more on leave
  }, [engagementId, currentPage]);

  const visiblePages = PAGES.filter((p) => !p.adminOnly || user?.isAdmin);
  const t = useT();

  return (
    <div
      className={`shell${collapsed && !isPhone ? ' shell--collapsed' : ''}${isPhone && drawerOpen ? ' shell--drawer-open' : ''}`}
    >
      <header className="topbar">
        <div className="topbar-left">
          <button
            className="btn-icon sidebar-toggle"
            onClick={toggleSidebar}
            aria-expanded={isPhone ? drawerOpen : undefined}
            aria-label={isPhone ? (drawerOpen ? t('Close menu') : t('Open menu')) : t('Toggle sidebar')}
            title={isPhone ? (drawerOpen ? t('Close menu') : t('Open menu')) : t('Toggle sidebar')}
          >☰</button>
          <div className="brand">
            <span className="brand-mark" />
            <span className="brand-name">SIGNET</span>
            <span className="brand-sep" />
            <span className="brand-page">{t(PAGES.find((p) => p.id === currentPage)?.title ?? '')}</span>
          </div>
        </div>
        <div className="topbar-right">
          <select
            className="form-select topbar-eng-select"
            value={current?.id ?? ''}
            onChange={(e) => setCurrentId(e.target.value ? Number(e.target.value) : null)}
            title={t('Active engagement')}
          >
            <option value="">{t('— no engagement —')}</option>
            {engagements.map((e) => (
              <option key={e.id} value={e.id}>{e.code} · {e.name}</option>
            ))}
          </select>
          <NotificationBell onNavigate={(hash: string) => { window.location.hash = hash; }} />
          <ContentLangControl />
          <LangSwitch />
          <button className="theme-btn" onClick={toggle} title={t('Toggle theme')}>{theme === 'dark' ? '◐' : '◑'}</button>
          <span className="user-tag"><span className="user-initial">{(user?.name || user?.email || '?')[0].toUpperCase()}</span></span>
          <button className="btn btn-ghost btn-sm logout-btn" onClick={() => void logout()} aria-label={t('Log out')} title={t('Log out')}>
            <span className="logout-icon" aria-hidden="true">⏻</span>
            <span className="logout-label">{t('Log out')}</span>
          </button>
        </div>
      </header>

      {isPhone && drawerOpen && <div className="shell-scrim" aria-hidden="true" onClick={closeDrawer} />}

      <aside className="sidebar">
        {NAV_GROUPS.map((group) => {
          const pages = visiblePages.filter((p) => p.navGroup === group);
          if (!pages.length) return null;
          return (
            <div className="sidebar-block" key={group}>
              <div className="sidebar-label">{t(group)}</div>
              {pages.map((p) => {
                const info = p.id === 'agent' ? foldActivity(activity.agent, activity.proposals) : activity[p.id];
                const count = info?.count ?? 0;
                const lastSeen = engagementId ? getLastSeen(engagementId, p.id) : null;
                // Accent dot = something changed since the operator last had the tab open.
                // Never on the active tab (they are looking at it right now).
                const hasDot =
                  p.id !== currentPage &&
                  !!info?.latest &&
                  (!lastSeen || new Date(info.latest).getTime() > new Date(lastSeen).getTime());
                return (
                  <a
                    key={p.id}
                    href={`#${p.id}`}
                    className={`nav-link${currentPage === p.id ? ' active' : ''}`}
                    aria-disabled={p.needsEngagement && !current}
                    style={p.needsEngagement && !current ? { opacity: 0.45, pointerEvents: 'none' } : undefined}
                    onClick={closeDrawer}
                  >
                    {t(p.title)}
                    {(count > 0 || hasDot) && (
                      <span className="nav-activity">
                        {count > 0 && (
                          <span
                            className="nav-activity-count"
                            title={`${count} ${count === 1 ? (COUNT_HINT[p.id]?.[0] ?? 'item waiting') : (COUNT_HINT[p.id]?.[1] ?? 'items waiting')}`}
                          >
                            {count > 99 ? '99+' : count}
                          </span>
                        )}
                        {hasDot && (
                          <span
                            className="nav-activity-dot"
                            aria-label="new activity"
                            title="Something changed here since you last looked"
                          />
                        )}
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          );
        })}
      </aside>

      <main className="content">{children}</main>
    </div>
  );
}
